import { Button, DropdownButton, Menu, Tooltip } from '@neo4j-ndl/react';
import { ChevronDownIconOutline, CommandLineIconOutline } from '@neo4j-ndl/react/icons';
import { APP_SCOPE } from '@nx/constants';
import { MODAL_TYPE, useModal, useToolsConfiguration } from '@nx/state';
import type { DbmsInfo } from '@nx/state/desktop';
import * as StdLib from '@nx/stdlib';
import { isNotNullish } from '@nx/stdlib';
import cx from 'classnames';
import { useReducer, useRef } from 'react';
import { useNavigate } from 'react-router';

import { ExploreIconOutline } from '../local-instance/explore-icon';
import type { useDbmsConnection } from '../local-instance/hooks/use-dbms-connection';
import { useToggleInstanceRunStatus } from '../local-instance/hooks/use-toggle-instance-run-status';

interface CompactConnectButtonProps {
  dbmsConnection: ReturnType<typeof useDbmsConnection>;
  canConnectOrDisconnect: boolean;
  instanceInfo: DbmsInfo;
}

export function CompactConnectButton({
  instanceInfo,
  dbmsConnection,
  canConnectOrDisconnect,
}: CompactConnectButtonProps) {
  const [isMenuVisible, toggleIsMenuVisible] = useReducer((s: boolean) => !s, false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const connectionFormModal = useModal(MODAL_TYPE.CONNECTION_FORM);

  const { toggleRunStatus, isStarting } = useToggleInstanceRunStatus(instanceInfo);

  const connectAuto = async () => {
    if (!canConnectOrDisconnect) {
      await toggleRunStatus();
    }

    dbmsConnection.connect();
  };

  const connectWithCredentials = async () => {
    if (!canConnectOrDisconnect) {
      await toggleRunStatus();
    }

    connectionFormModal.open({
      instanceDetails: {
        id: instanceInfo.id,
        name: instanceInfo.name,
        isAuraInstanceFromAPI: false,
      },
      initialValues: {
        username: '',
        protocol: StdLib.URLs.PROTOCOL_NEO4J,
        hostname: StdLib.URLs.Neo4jURL.asNullable(instanceInfo.connectionUri)?.host ?? '',
      },
      isExternal: false,
      warning: undefined,
      error: undefined,
      hideConnectionMethods: true,
    });
  };

  return (
    <>
      <span className="m-0 flex gap-[1px]">
        <Button
          onClick={() => {
            if (dbmsConnection.isConnected) {
              dbmsConnection.disconnect();
            } else {
              void connectAuto();
            }
          }}
          isLoading={isStarting || dbmsConnection.isLoading}
          className="rounded-r-none"
        >
          {dbmsConnection.isConnected ? 'Disconnect' : 'Connect'}
        </Button>
        <Button
          ref={menuButtonRef}
          onClick={toggleIsMenuVisible}
          size="medium"
          className="bg-palette-primary-bg-strong hover:bg-palette-primary-hover-strong rounded-l-none"
          style={{ padding: '8px' }}
        >
          <ChevronDownIconOutline
            className={cx('stroke-palette-neutral-text-inverse h-4 w-4 shrink-0', {
              'rotate-180': isMenuVisible,
            })}
          />
        </Button>
      </span>
      <Menu
        isOpen={isMenuVisible}
        anchorRef={menuButtonRef}
        onClose={toggleIsMenuVisible}
        // TODO: Use better method of displaying menu above modal
        className="z-60"
      >
        <Menu.Items>
          <Menu.Item
            title="Connect with credentials"
            onClick={() => {
              void connectWithCredentials();
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
}

interface SplitConnectButtonProps {
  dbmsConnection: ReturnType<typeof useDbmsConnection>;
  canConnectOrDisconnect: boolean;
  instanceInfo: {
    id: string;
    name: string;
    connectionUri: string;
  };
}

export function SplitConnectButton({ dbmsConnection, canConnectOrDisconnect }: SplitConnectButtonProps) {
  const [isMenuVisible, toggleIsMenuVisible] = useReducer((s: boolean) => !s, false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Tooltip type="simple" isDisabled={canConnectOrDisconnect} isPortaled={false}>
      <Tooltip.Trigger hasButtonWrapper>
        <span className="m-0 flex h-8 gap-[1px]">
          <Button
            onClick={dbmsConnection.isConnected ? dbmsConnection.disconnect : dbmsConnection.connect}
            isDisabled={!canConnectOrDisconnect}
            isLoading={dbmsConnection.isLoading}
            className="rounded-r-none"
          >
            {dbmsConnection.isConnected ? 'Disconnect' : 'Connect'}
          </Button>
          <Button
            ref={menuButtonRef}
            isDisabled={!canConnectOrDisconnect || dbmsConnection.isLoading}
            onClick={toggleIsMenuVisible}
            size="medium"
            className="bg-palette-primary-bg-strong hover:bg-palette-primary-hover-strong rounded-l-none"
            style={{ padding: '8px' }}
          >
            <ChevronDownIconOutline
              className={cx('stroke-palette-neutral-text-inverse h-4 w-4 shrink-0', {
                'rotate-180': isMenuVisible,
              })}
            />
          </Button>
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content>Instance is not running.</Tooltip.Content>
      <Menu
        isOpen={isMenuVisible}
        anchorRef={menuButtonRef}
        onClose={toggleIsMenuVisible}
        // TODO: Use better method of displaying menu above modal
        className="z-60"
      >
        <Menu.Items>
          <Menu.Item title="Connect with credentials" onClick={dbmsConnection.openConnectWithCredentialsModal} />
        </Menu.Items>
      </Menu>
    </Tooltip>
  );
}

interface ConnectDropdownButtonProps {
  dbmsConnection: ReturnType<typeof useDbmsConnection>;
  canConnectOrDisconnect: boolean;
}
export function ConnectDropdownButton({ dbmsConnection, canConnectOrDisconnect }: ConnectDropdownButtonProps) {
  const menuRef = useRef(null);
  const [isMenuVisible, toggleIsMenuVisible] = useReducer((s: boolean) => !s, false);
  const navigate = useNavigate();
  const toolsConfiguration = useToolsConfiguration();

  return (
    <>
      <DropdownButton
        ref={menuRef}
        isOpen={isMenuVisible}
        onClick={toggleIsMenuVisible}
        size="medium"
        isDisabled={!canConnectOrDisconnect}
        htmlAttributes={{
          'aria-label': 'Connection menu',
        }}
      >
        Connect
      </DropdownButton>
      <Menu isOpen={isMenuVisible} anchorRef={menuRef} onClose={toggleIsMenuVisible}>
        <Menu.Items>
          <Menu.Item
            title="Query"
            icon={<CommandLineIconOutline />}
            isDisabled={!canConnectOrDisconnect}
            onClick={() => {
              dbmsConnection.connect();
              if (isNotNullish(toolsConfiguration[APP_SCOPE.query])) {
                void navigate(toolsConfiguration[APP_SCOPE.query].route);
              }
            }}
          />
          <Menu.Item
            title="Explore"
            icon={<ExploreIconOutline />}
            isDisabled={!canConnectOrDisconnect}
            onClick={() => {
              dbmsConnection.connect();
              if (isNotNullish(toolsConfiguration[APP_SCOPE.explore])) {
                void navigate(toolsConfiguration[APP_SCOPE.explore].route);
              }
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
}
